import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import ReactPlayer from 'react-player';
import touchScreenIcon from '../../../assets/icons/touchScreenIcon.png';
import { baseSoundURL } from '../../../config';
import { TerminalScreensaverMedia } from '../../../types/Admin/TerminalAppearanceTypes';
import classes from './TerminalScreensaver.module.scss';

type TerminalScreensaverProps = {
  media: TerminalScreensaverMedia[];
  imageSlideSeconds?: number;
};

const DEFAULT_IMAGE_SLIDE_SECONDS = 8;

const normalizeUrl = (url: string) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) {
    return url;
  }
  const separator = url.startsWith('/') ? '' : '/';
  return `${baseSoundURL}${separator}${url}`;
};

type ScreensaverSlideProps = {
  item: TerminalScreensaverMedia;
  url: string;
  visible: boolean;
  playing: boolean;
  onReady: () => void;
  onEnded?: () => void;
};

const ScreensaverSlide: FC<ScreensaverSlideProps> = ({
  item,
  url,
  visible,
  playing,
  onReady,
  onEnded,
}) => {
  const isVideo = item.mediaType === 'VIDEO';
  const readyNotifiedRef = useRef(false);

  useEffect(() => {
    readyNotifiedRef.current = false;
  }, [item.id, url]);

  useEffect(() => {
    if (!isVideo && !readyNotifiedRef.current) {
      readyNotifiedRef.current = true;
      onReady();
    }
  }, [isVideo, onReady]);

  const handleVideoReady = () => {
    if (readyNotifiedRef.current) {
      return;
    }
    readyNotifiedRef.current = true;
    onReady();
  };

  return (
    <div
      className={classNames(
        classes.slide,
        visible ? classes.slideVisible : classes.slideHidden,
      )}
      aria-hidden={!visible}
    >
      {isVideo ? (
        <div className={classes.videoWrap}>
          <ReactPlayer
            url={url}
            playing={playing}
            muted
            width="100%"
            height="100%"
            onReady={handleVideoReady}
            onEnded={onEnded}
          />
        </div>
      ) : (
        <div
          className={classes.image}
          style={{ backgroundImage: `url("${url}")` }}
        />
      )}
    </div>
  );
};

export const TerminalScreensaver: FC<TerminalScreensaverProps> = ({
  media,
  imageSlideSeconds = DEFAULT_IMAGE_SLIDE_SECONDS,
}) => {
  const items = useMemo(() => media.filter((m) => m.url), [media]);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const displayIndexRef = useRef(0);

  useEffect(() => {
    displayIndexRef.current = 0;
    setDisplayIndex(0);
    setPendingIndex(null);
  }, [items]);

  const queueNext = useCallback(() => {
    if (items.length <= 1 || pendingIndex !== null) {
      return;
    }
    const next = (displayIndexRef.current + 1) % items.length;
    setPendingIndex(next);
  }, [items.length, pendingIndex]);

  const activatePending = useCallback(() => {
    setPendingIndex((pending) => {
      if (pending === null) {
        return null;
      }
      displayIndexRef.current = pending;
      setDisplayIndex(pending);
      return null;
    });
  }, []);

  const slideDurationMs = Math.max(imageSlideSeconds, 0) * 1000;

  useEffect(() => {
    if (items.length <= 1 || slideDurationMs <= 0 || pendingIndex !== null) {
      return;
    }
    const current = items[displayIndex];
    if (current?.mediaType === 'VIDEO') {
      return;
    }
    const timer = window.setTimeout(queueNext, slideDurationMs);
    return () => window.clearTimeout(timer);
  }, [displayIndex, items, pendingIndex, queueNext, slideDurationMs]);

  const touchHint = (
    <div
      className={classes.touchHint}
      aria-hidden
      title="Коснитесь экрана, чтобы продолжить"
    >
      <img
        className={classes.touchHintIcon}
        src={touchScreenIcon}
        alt=""
        draggable={false}
      />
    </div>
  );

  if (!items.length) {
    return (
      <div className={classes.root}>
        <div className={classes.fallback} />
        {touchHint}
      </div>
    );
  }

  const slideIndices = new Set<number>([displayIndex]);
  if (pendingIndex !== null) {
    slideIndices.add(pendingIndex);
  }

  return (
    <div className={classes.root}>
      {items.map((item, index) => {
        if (!slideIndices.has(index)) {
          return null;
        }

        const isDisplay = index === displayIndex;
        const isPending = index === pendingIndex;
        const visible = isDisplay;
        const playing = isPending || (isDisplay && pendingIndex === null);

        return (
          <ScreensaverSlide
            key={item.id}
            item={item}
            url={normalizeUrl(item.url)}
            visible={visible}
            playing={playing}
            onReady={isPending ? activatePending : () => undefined}
            onEnded={isDisplay ? queueNext : undefined}
          />
        );
      })}
      {touchHint}
    </div>
  );
};
